import { REPORT_WIDGET_DND } from './ReportWidget';
import { useDrop } from 'react-dnd';
import { useRef } from 'react';

// The responsive widget grid: equal-width columns, min 400px, wrapping. Exported
// so the build-phase preview can reuse the exact layout without the drag/drop
// wiring this component adds.
export const WIDGET_GRID_CLASS =
    'grid grid-cols-[repeat(auto-fit,minmax(min(400px,100%),1fr))] gap-4';

// The widget grid in edit mode. A single container-level drop target maps the
// dragged card's center to the nearest slot (closest-center), so a swap fires at
// ~50% overlap and works across rows. A per-item cursor hover can't do this: the
// grab handle sits at the card's far left, so the mouse only enters the next card
// once the dragged card already ~fully overlaps it (the swap then felt very late).
//
// `slotCount` bounds the reorder targets to the leading widget cells, so a
// trailing non-widget cell (the "add widget" tile) is never a drop target — its
// DOM child would otherwise map to an out-of-range widget index.
function ReportWidgetGrid({ onMove, slotCount, children }) {
    const containerRef = useRef(null);

    const [, drop] = useDrop({
        accept: REPORT_WIDGET_DND,
        hover: (item, monitor) => {
            const container = containerRef.current;
            const diff = monitor.getDifferenceFromInitialOffset();
            if (!container || !diff) return;

            const centerX = item.startCenterX + diff.x;
            const centerY = item.startCenterY + diff.y;

            // Closest-center: the slot whose center is nearest the dragged card's
            // live center wins. children[i] aligns with the i-th widget.
            let target = item.index;
            let best = Infinity;
            const slots = Array.from(container.children).slice(
                0,
                slotCount ?? container.children.length,
            );
            slots.forEach((child, i) => {
                const r = child.getBoundingClientRect();
                const dx = r.left + r.width / 2 - centerX;
                const dy = r.top + r.height / 2 - centerY;
                const dist = dx * dx + dy * dy;
                if (dist < best) {
                    best = dist;
                    target = i;
                }
            });

            if (target !== item.index) {
                onMove(item.index, target);
                item.index = target; // keep in sync so the next hover compares right
            }
        },
    });

    return (
        <div
            ref={(node) => {
                containerRef.current = node;
                drop(node);
            }}
            className={WIDGET_GRID_CLASS}
        >
            {children}
        </div>
    );
}

export default ReportWidgetGrid;
